import React, { useState, useCallback, useEffect } from 'react';
import { filter, findIndex, get, noop, startCase } from 'lodash';
import { Paper, Stack, Typography, Grid, Select, MenuItem } from '@mui/material';
import Form from 'mobx-react-form';
import { observer } from 'mobx-react-lite';

import { withMicrofrontend } from 'utils/microfrontend/useMicrofrontend';
import { fetchExpenseCompsVersionsFromCompPlex } from 'utils/comp-plex/api';

import { BASIS_OF_COMPARISON_ITEMS, DEFAULT_EXPENSES_KEYS, EXPENSE_COMPARABLES_INFO } from 'shared/constants/expenses';

import ComparableExpensesTable from './ComparableExpensesTable';
import ComparableExpensesPanel from './ComparableExpensesPanel';
import { comparableExpenseFormHelpers } from './comparableExpenseFormConfig';

import { EntryMode, ExpenseComp } from 'types/underwriting/income-approach/expenses';
import { useRootStore } from 'stores/RootStore';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { EXPENSE_SUMMARIZED_ENTRY_MODE } from 'shared/constants/expenses';
import { ExpenseCategory } from '../types';

type ComparableExpensesProps = {
  compPlexLoaded?: boolean;
  form: Form;
};
const ComparableExpenses = observer(({ compPlexLoaded, form }: ComparableExpensesProps) => {
  const [basisOfComparison, setBasisOfComparison] = useState('Gross');
  const [latestCompVersions, setLatestCompVersions] = useState<Record<string, number>>({});

  const { underwritingPageStore } = useRootStore();
  const { underwritingData = {} as UnderwritingOutput } = underwritingPageStore;
  const { property } = underwritingData || {};
  const { residentialUnits = 0, grossBuildingArea = 0, propertyType = 'mixed-use' } = property || {};

  const expenseHistoryEntryMode: EntryMode | undefined = get(
    underwritingData,
    'incomeApproach.expenses.expenseHistoryEntryMode'
  );

  const expenseCategoriesField = form.$('incomeApproach.expenses.expenseCategories');
  const expenseCategories = expenseCategoriesField.value;

  const filteredExpenseCategories =
    expenseHistoryEntryMode !== EXPENSE_SUMMARIZED_ENTRY_MODE
      ? expenseCategories
      : filter(expenseCategories, (category: ExpenseCategory) =>
          Object.values(DEFAULT_EXPENSES_KEYS).includes(category.key)
        );

  const expenseInfoCategories = EXPENSE_COMPARABLES_INFO.map((infoKey) => {
    return {
      key: infoKey,
      category: startCase(infoKey),
    };
  });
  const allTableCategories = [...expenseInfoCategories, ...filteredExpenseCategories];

  const expenseCompsField = form.$('incomeApproach.expenses.expenseComps');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const expenseComps = expenseCompsField.value || [];

  const onError = noop;

  const loadLatestExpensesVersions = useCallback(async () => {
    if (!compPlexLoaded) return;

    const ids = expenseComps.map((expense: any) => expense.boweryId).filter(Boolean);

    if (ids.length) {
      try {
        const { comps } = await fetchExpenseCompsVersionsFromCompPlex(ids);
        const versionsMap = comps.reduce((acc: Record<string, number>, comp: any) => {
          acc[comp.id] = comp.version;
          return acc;
        }, {});
        setLatestCompVersions(versionsMap);
      } catch (error) {
        console.error('Failed to load latest expense versions:', error);
      }
    }
  }, [compPlexLoaded, expenseComps]);

  useEffect(() => {
    loadLatestExpensesVersions();
  }, [loadLatestExpensesVersions]);

  const addExpenseComparable = (expenseComp: ExpenseComp) => {
    try {
      comparableExpenseFormHelpers.addExpenseComp(form, expenseComp);
    } catch (error) {
      console.error('Could not add new expense comp: ', error);
    }
  };

  const deleteExpenseComparable = useCallback(
    (compId: string) => {
      const index = findIndex(expenseComps, (comp: ExpenseComp) => comp.id === compId);
      comparableExpenseFormHelpers.removeExpenseComp(form, index);
    },
    [expenseComps, form]
  );

  return (
    <div>
      <Grid container spacing={1}>
        <Stack sx={{ width: '100%', position: 'relative' }}>
          <ComparableExpensesPanel
            errorNotification={onError}
            propertySummary={{ residentialUnits, grossBuildingArea }}
            subjectExpenses={undefined}
            comparableExpenses={expenseComps}
            onAddExpenseComps={function (comps: ExpenseComp[]): void {
              comps.forEach((comp) => {
                addExpenseComparable(comp);
              });
            }}
          />
        </Stack>

        <Grid item xs={12} sm={12}>
          <Grid item>
            <Paper sx={{ padding: 3, marginBottom: 3 }}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  Expense Comparables
                </Typography>
              </Grid>

              <Grid container direction="row" justifyContent="space-between">
                <Grid item sx={{ pb: 2 }}>
                  <Select
                    value={basisOfComparison}
                    onChange={(event) => setBasisOfComparison(event.target.value)}
                    label="Basis of Comparison"
                    id="basis-of-comparison"
                    size="small"
                    sx={{ width: '200px', fontSize: '14px' }}
                  >
                    {BASIS_OF_COMPARISON_ITEMS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>

              <ComparableExpensesTable
                form={form}
                propertyType={propertyType}
                deleteColumn={deleteExpenseComparable}
                basis={basisOfComparison}
                expenseCategories={allTableCategories}
                latestCompVersions={latestCompVersions}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
});

export default withMicrofrontend('compplex', 'compplexLoaded')(ComparableExpenses);
